import LAYOUT_API from '@frontend/services/api/layout';

/**
 * Generates the thumbnails that are missing from the layout thumbnail grid.
 *
 * Every item without a thumbnail renders a hidden loader and a hidden failure notice (see
 * thumbnail-layout-item.blade.php). As soon as a loader scrolls into view we reveal it and ask the
 * backend to render the thumbnail from the first template of that layout, then swap the spinner for
 * either the thumbnail or the failure notice. The requests are throttled because a single render is
 * an expensive PDF/PNG export on the server.
 */

const LOADER_SELECTOR = '[data-generate-thumbnail-layout-id]';
const MAX_CONCURRENT_REQUESTS = 2;

// layoutId -> 'pending' | 'failed' | thumbnail url. Kept across Livewire re-renders so that
// paginating back and forth does not re-request the very same thumbnail.
const results = new Map();
const queue = [];

// The loader itself starts hidden, so it can never intersect: we watch its visible wrapper instead.
const loaderByWrapper = new WeakMap();

let runningRequests = 0;
let observer = null;
let initialised = false;

function getLoaders(layoutId) {
  return Array.from(document.querySelectorAll(`[data-generate-thumbnail-layout-id="${layoutId}"]`));
}

function applyThumbnail(loader, url) {
  const image = loader.closest('.layout-item__image')?.querySelector('img');

  if (image) {
    image.src = url;
  }

  loader.remove();
}

/**
 * Swaps the spinner for the "generation failed" notice that sits next to it in the markup.
 */
function applyError(loader) {
  const error = loader
    .closest('.layout-item__image')
    ?.querySelector('[data-thumbnail-error-layout-id]');

  if (error) {
    error.hidden = false;
  }

  loader.remove();
}

function resolveLayout(layoutId, url) {
  results.set(layoutId, url);
  getLoaders(layoutId).forEach((loader) => applyThumbnail(loader, url));
}

function failLayout(layoutId) {
  results.set(layoutId, 'failed');
  getLoaders(layoutId).forEach(applyError);
}

function processQueue() {
  while (runningRequests < MAX_CONCURRENT_REQUESTS && queue.length) {
    const layoutId = queue.shift();

    runningRequests += 1;

    LAYOUT_API.generateLayoutThumbnail(layoutId)
      .then(({ data }) => {
        if (data?.success && data?.url) {
          resolveLayout(layoutId, data.url);
        } else {
          failLayout(layoutId);
        }
      })
      .catch(() => failLayout(layoutId))
      .finally(() => {
        runningRequests -= 1;
        processQueue();
      });
  }
}

function enqueue(loader) {
  const layoutId = loader.dataset.generateThumbnailLayoutId;

  if (!layoutId) {
    return;
  }

  const known = results.get(layoutId);

  // A Livewire re-render brings the pristine markup back, so the failed state has to be re-applied.
  if (known === 'failed') {
    applyError(loader);

    return;
  }

  if (known && known !== 'pending') {
    applyThumbnail(loader, known);

    return;
  }

  loader.hidden = false;

  if (known === 'pending') {
    // Another item of the same layout is already waiting for the very same request.
    return;
  }

  results.set(layoutId, 'pending');
  queue.push(layoutId);
  processQueue();
}

function getObserver() {
  if (observer) {
    return observer;
  }

  // The thumbnail grid lives in a container that is hidden while the table view is active, so
  // an intersection observer also keeps us from generating thumbnails nobody is looking at.
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      observer.unobserve(entry.target);

      const loader = loaderByWrapper.get(entry.target);

      if (loader?.isConnected) {
        enqueue(loader);
      }
    });
  }, { rootMargin: '200px' });

  return observer;
}

/**
 * Picks up the loaders of the current DOM. Safe to call again after every Livewire re-render.
 */
export function scanForMissingLayoutThumbnails() {
  document.querySelectorAll(LOADER_SELECTOR).forEach((loader) => {
    const wrapper = loader.closest('.layout-item__image');

    if (!wrapper || typeof IntersectionObserver === 'undefined') {
      enqueue(loader);

      return;
    }

    loaderByWrapper.set(wrapper, loader);
    getObserver().observe(wrapper);
  });
}

/**
 * Starts watching the thumbnail grid, including the items brought in by pagination, filtering or
 * any other Livewire re-render. Can be called from every page that renders the grid.
 */
export function initAutoLayoutThumbnails() {
  if (initialised) {
    return;
  }

  initialised = true;

  const registerHook = () => {
    window.livewire?.hook('message.processed', (message, component) => {
      if (component.fingerprint.name === 'thumbnail-layout-list') {
        scanForMissingLayoutThumbnails();
      }
    });
  };

  // This runs both from inside a livewire:load handler and from a plain entry point script.
  if (window.livewire) {
    registerHook();
  } else {
    document.addEventListener('livewire:load', registerHook);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForMissingLayoutThumbnails);
  } else {
    scanForMissingLayoutThumbnails();
  }
}
